import { Container, Typography } from '@cleartrip/ct-design-components'
import { memo, type ReactNode, useEffect, useRef } from 'react'

import HighRpsDiscount from '@/assets/svg/HighRpsDiscount'
import HighRpsOfferCustom from '@/assets/svg/HighRpsOfferCustom'
import { formatCurrency } from '@/utils/general'

import { HIDE_OPTIONS, highRpsMoreOptionsLabel, highRpsStartingFromLabel } from '../config'
import ExpandableTupleGroup, { scrollAwareCollapse } from './ExpandableTupleGroup'
import TupleGroupFooter from './TupleGroupFooter'
import { HIGH_RPS_THEME, TUPLE_COLORS } from '../config/tupleTheme'

export interface HighRpsGroupProps {
  children: ReactNode
  startingPrice: number
  extraCount: number
  expanded: boolean
  onExpand: () => void
  onCollapse: () => void
  siblings?: ReactNode
}

const HighRpsHeader: React.FC<{ startingPrice: number }> = ({ startingPrice }) => (
  <Container className='flex flex-middle flex-gap-1 pb-3 w-100p'>
    <Container className='flex flex-middle flex-no-shrink' style={{ height: 24, width: 24 }}>
      <HighRpsDiscount />
    </Container>
    <Typography color='heading' variant='HM4'>
      {highRpsStartingFromLabel(formatCurrency(startingPrice))}
    </Typography>
  </Container>
)

const HighRpsWatermark = () => (
  <Container className='p-absolute' style={{ top: 0, right: 0, pointerEvents: 'none' }}>
    <HighRpsOfferCustom color={TUPLE_COLORS.brand} height={93} width={96} />
  </Container>
)

const HighRpsGroup: React.FC<HighRpsGroupProps> = ({
  children,
  startingPrice,
  extraCount,
  expanded,
  onExpand,
  onCollapse,
  siblings = null,
}) => {
  const groupRef = useRef<HTMLDivElement>(null)
  const collapseTimerRef = useRef<NodeJS.Timeout | null>(null)
  const canExpand = extraCount > 0

  useEffect(() => {
    return () => {
      const timer = collapseTimerRef.current
      if (timer) clearTimeout(timer)
    }
  }, [])

  const handleCollapse = () => scrollAwareCollapse(groupRef.current, onCollapse, collapseTimerRef)

  return (
    <ExpandableTupleGroup
      active
      collapsedFooter={
        canExpand ? (
          <TupleGroupFooter label={highRpsMoreOptionsLabel(extraCount)} variant='expand' onClick={onExpand} />
        ) : null
      }
      expanded={expanded && canExpand}
      expandedFooter={
        canExpand ? <TupleGroupFooter label={HIDE_OPTIONS} variant='collapse' onClick={handleCollapse} /> : null
      }
      groupRef={groupRef}
      header={<HighRpsHeader startingPrice={startingPrice} />}
      siblings={canExpand ? siblings : null}
      theme={HIGH_RPS_THEME}
      watermark={<HighRpsWatermark />}
    >
      {children}
    </ExpandableTupleGroup>
  )
}

export default memo(HighRpsGroup)
